import { IField } from "@/api/AppApi/services/builder/interfaces/interfaces";
import { useGetDialCodesQuery } from "@/api/AppApi/services/company";
import { ChangeEvent, useEffect, useState } from "react";
import { Row } from "@/utils/bootstrap";
import { Controller, FieldValues, UseFormReturn } from "react-hook-form";
import HookInput from "../hook-input/HookInput";
import SelectCountryCode from "./country-code/SelectCountryCode";
import { validateFormController } from "../../form-builder-helpers/form-functions";
import InputErrorMsg from "../../form-builder-helpers/InputErrorMsg";
import CustomButton, {
  BtnRadius,
  BtnSizes,
  BtnTypes,
} from "@/app/components/lib/CustomButton";
import SpinnerForBtn from "@/app/components/lib/loaders/SpinnerForBtn";
import Flag from "@/app/components/lib/Flag";
import { useTranslation } from "@/hooks/localization/useTranslations";

interface Props {
  form: UseFormReturn<FieldValues | any, any>;
  field: IField;
  viewPencil?: boolean;
  verifiedPosition?: number | null;
}
const validPhoneDigits = (val: string) =>
  val === "" || /^[1-9][0-9]*$/.test(val);

const HookPhoneNumber: React.FC<Props> = ({
  form,
  field,
  viewPencil = false,
  verifiedPosition = null,
}) => {
  const t = useTranslation();
  const [show, setShow] = useState(false);
  const getDialCodes = useGetDialCodesQuery();
  const dialCodes = getDialCodes.data?.result;

  const fConfig = field.configurations;
  const fieldName = fConfig?.key;
  const numberField = `${fieldName}.number`;
  const dialField = `${fieldName}.dial`;
  const dialCountryCode = form.watch(dialField);

  useEffect(() => {
    if (dialCountryCode != null || dialCountryCode != undefined) return;
    if (getDialCodes.isLoading) return;
    setTimeout(() =>
      form.setValue(
        dialField,
        dialCodes?.find((c) => c.selected) ?? dialCodes?.[0],
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDialCodes.isLoading, dialCountryCode]);

  return (
    <>
      <div className="input-group" dir="ltr">
        <SelectCountryCode
          show={show}
          setShow={setShow}
          name={dialField}
          dialCountryCode={dialCountryCode}
          form={form}
        />
        <div className="input-group-prepend">
          <div
            className="input-group-btn"
            id="inputGroupPrepend2  d-flex flex-column"
          >
            <CustomButton
              type={BtnTypes.neutral}
              size={BtnSizes.semi_small}
              radius={BtnRadius.semi_small}
              onClick={() => setShow(true)}
            >
              {getDialCodes.isLoading ? (
                <SpinnerForBtn dark />
              ) : (
                <>
                  <Flag countryCode={dialCountryCode?.iso2} width={18} />
                  {dialCountryCode?.iso2?.toUpperCase() + " "}
                  {dialCountryCode?.dial_code}
                </>
              )}
            </CustomButton>
          </div>
        </div>

        <Controller
          control={form.control}
          name={numberField}
          rules={{
            validate: (value) =>
              validateFormController({
                form,
                fieldName: fConfig.key,
                value,
                is_required: fConfig?.is_required,
                requiredMsg: t.this_field_is_required,
              }),
            required: true,
          }}
          render={({ field }) => (
            <HookInput
              inputRegister={{
                //special use
                ...field,
                value: field.value ?? "",
                onChange: (e: ChangeEvent<HTMLInputElement>) =>
                  validPhoneDigits(e.target.value) &&
                  field.onChange(e.target.value),
              }}
              type="tel"
              dir="ltr"
              label={fConfig.title}
              errorMsg={undefined}
              viewPencil={viewPencil}
              verifiedPosition={verifiedPosition}
              placeholder={fConfig.placeholder}
              hideLabel={fConfig.hideLabel}
              required={fConfig.is_required}
            />
          )}
        />
      </div>
      <Row className="m-0 p-0" dir="auto">
        {fConfig?.hint && <div className="field-hint">{fConfig?.hint}</div>}
        {form.formState.errors[fieldName] && (
          <InputErrorMsg
            errorMsg={form.formState.errors[fieldName]?.message as string}
          />
        )}
      </Row>
    </>
  );
};

export default HookPhoneNumber;
